import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { HttpError } from '@rd/types';
import { Promocode, PromocodeUsage } from '@rd/types/promocode.type';
import { Observable } from 'rxjs';
import * as actions from '../../actions/cart-item.actions';
import { CartItemModuleState } from '../../reducers';
import {
  getAvailableCartItemsArray,
  getCartItemAddLoading,
  getCartItemIndexError,
  getCartItemIndexLoading,
  getCartItemIndexRefreshing,
  getCartItemsArray,
  getCartItemsDeliveryMethods,
  getCartItemsShouldHaveAddressType,
  getCartItemsTotal,
  getCartItemsTotalLineItems,
  getCartPromocode,
  getCartPromocodeError,
  getCartPromocodeLoadingState,
  getCartPromocodeUsage,
  getUnAvailableCartItemsArray,
} from '../../selectors/cart-item.selectors';
import { CartItemUpdateQuantityParams } from '../../services/cart-item-api/cart-item-api.service';
import {
  AddCartItemParam,
  CartItem,
  CartItemTotal,
} from '../../types/cart-item.type';
import { CartPromocodePostParams } from '../../types/cart.type';
import { DeliveryMethod } from '../../types/delivery-method.type';

@Injectable({ providedIn: 'root' })
export class CartItemFacade {
  cartItems$: Observable<CartItem[]> = this.store.select(getCartItemsArray);
  availableCartItems$: Observable<CartItem[]> = this.store.select(
    getAvailableCartItemsArray,
  );

  unAvailableCartItems$: Observable<CartItem[]> = this.store.select(
    getUnAvailableCartItemsArray,
  );

  cartItemsLoading$: Observable<boolean> = this.store.select(
    getCartItemIndexLoading,
  );

  cartItemsRefreshing$: Observable<boolean> = this.store.select(
    getCartItemIndexRefreshing,
  );

  cartItemsError$: Observable<HttpError | null> = this.store.select(
    getCartItemIndexError,
  );

  cartItemsTotalLineItems$: Observable<number> = this.store.select(
    getCartItemsTotalLineItems,
  );

  cartItemsTotal$: Observable<CartItemTotal> =
    this.store.select(getCartItemsTotal);

  cartItemsDeliveryMethods$: Observable<DeliveryMethod[]> = this.store.select(
    getCartItemsDeliveryMethods,
  );

  shouldHaveAddressType$: Observable<boolean> = this.store.select(
    getCartItemsShouldHaveAddressType,
  );

  addCartItemLoading$: Observable<boolean> = this.store.select(
    getCartItemAddLoading,
  );

  // Actions

  constructor(private store: Store<CartItemModuleState>) {}

  updateCartItemQuantity({ id, quantity }: CartItemUpdateQuantityParams): void {
    this.store.dispatch(actions.UpdateCartItemQuantity({ id, quantity }));
  }

  deleteCartItem(id: CartItem['id']): void {
    this.store.dispatch(actions.RemoveCartItems({ id }));
  }

  addCartItem(cartItem: AddCartItemParam): void {
    this.store.dispatch(actions.AddCartItem(cartItem));
  }

  // Cart api related facade methods
  loadCart(): void {
    this.store.dispatch(actions.LoadCart());
  }

  loadCartAfterAddingItem(addToCartPromptDialogData: {
    dialogTitle: string;
    productName: string;
    quantity: number;
  }): void {
    this.store.dispatch(
      actions.LoadCartAfterAddingItem({ addToCartPromptDialogData }),
    );
  }

  addCartPromocode(cartPromocode: CartPromocodePostParams): void {
    this.store.dispatch(actions.AddCartPromocode(cartPromocode));
  }

  removeCartPromocode(): void {
    this.store.dispatch(actions.RemoveCartPromocode());
  }

  removeCartExpiredPromocode(): void {
    this.store.dispatch(actions.RemoveCartExpiredPromocode());
  }

  resetCartPromocodeError(): void {
    this.store.dispatch(actions.ResetCartPromocodeError());
  }

  cartPromocode$: Observable<Promocode | null> =
    this.store.select(getCartPromocode);

  cartPromocodeUsage$: Observable<PromocodeUsage | null> = this.store.select(
    getCartPromocodeUsage,
  );

  cartPromocodeLoading$: Observable<boolean> = this.store.select(
    getCartPromocodeLoadingState,
  );

  cartPromocodeError$: Observable<HttpError | null> = this.store.select(
    getCartPromocodeError,
  );
}
